import { useCallback } from "react";
import { message } from "antd";

import useAdminHttp from "./useAdminHttp";

/**
 * Admin-only hooks for account deletion monitoring APIs (Redis-backed status).
 */
export default function useAccountDeletionMonitoring() {
  const { fetchData, isLoading } = useAdminHttp();

  const fetchRecentDeletionStatuses = useCallback(
    async (page, pageSize, { status: statusValue = "", q: qValue = "" } = {}) => {
      const params = new URLSearchParams();
      params.set("page", String(page));
      params.set("pageSize", String(pageSize));
      if (statusValue) {
        params.set("status", statusValue);
      }
      if ((qValue || "").trim()) {
        params.set("q", (qValue || "").trim());
      }
      const resp = await fetchData(`account-deletion/status/recent?${params.toString()}`, "get", null);
      if (!resp || resp?.data === undefined) {
        message.error("No response from server");
        return null;
      }
      const payload = resp.data;
      if (payload.responseStatus !== 200) {
        message.error(payload.responseMessage || "Failed to load recent deletion statuses");
        return null;
      }
      return payload;
    },
    [fetchData],
  );

  const fetchDeletionStatusByOsyncId = useCallback(
    async (osyncId) => {
      const params = new URLSearchParams();
      params.set("osyncId", osyncId);
      const resp = await fetchData(`account-deletion/status?${params.toString()}`, "get", null);
      if (!resp || resp?.data === undefined) {
        message.error("No response from server");
        return null;
      }
      return resp.data;
    },
    [fetchData],
  );

  const requestAdminDeleteOtp = useCallback(
    async (osyncId) => {
      const params = new URLSearchParams();
      params.set("osyncId", osyncId);
      const resp = await fetchData(`delete-account/request-otp?${params.toString()}`, "post", null);
      if (!resp || resp?.data === undefined) {
        message.error("No response from server");
        return null;
      }
      return resp.data;
    },
    [fetchData],
  );

  const confirmAdminDeleteAccount = useCallback(
    async (osyncId, otp) => {
      const params = new URLSearchParams();
      params.set("osyncId", osyncId);
      params.set("otp", otp);
      const resp = await fetchData(`delete-account?${params.toString()}`, "delete", null);
      if (!resp || resp.data === undefined) {
        message.error("No response from server");
        return null;
      }
      return resp;
    },
    [fetchData],
  );

  return {
    fetchRecentDeletionStatuses,
    fetchDeletionStatusByOsyncId,
    requestAdminDeleteOtp,
    confirmAdminDeleteAccount,
    isLoading,
  };
}
